/// <amd-module name="Search/Wiki/Components/ItemsList" />

import * as React from "react";
import * as Resources from "Search/Wiki/Resources";
import * as SharedResources from "Search/Shared/Resources";
import { routeUrl } from "VSS/Platform/Location";
import { WikiResult } from "Search/WebApi/Contracts/Search.Shared";
import { sanitizeHtml, UrlEscapeConstants } from "Search/Wiki/WikiUtils";
import { WikiSearchResultRow } from "SearchUI/WikiSearchResultItem";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { removeExtensionfromPagePath } from "SearchUI/Helpers/WikiHelper";
import { List, IListItemDetails, ListItem } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { FocusZoneDirection } from "VSSUI/FocusZone";

export interface IItemsListProps {
    items: WikiResult[];

    pageContext: IVssPageContext;

    itemsLoading: boolean;

    notifyOnRendered(): void;

    /**
    * On click of file name
    */
    onFileNameClicked: (link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => void;

    renderLoadingRow(): JSX.Element;
}

export class ItemsList extends React.Component<IItemsListProps, {}> {
    private isRendered = false;

    public render(): JSX.Element {
        const { items } = this.props;
        return (
            <List
                className="items-list"
                itemProvider={new ArrayItemProvider(items)}
                renderRow={this.rowRenderer}
                focuszoneProps={{
                    focusOnMount: false,
                    disabled: false,
                    direction: FocusZoneDirection.Vertical,
                    handleTabKey: true
                }}
                selectableText={true}
                width="100%"
                ariaLabel={SharedResources.SearchResultsListAriaLabel} />
        );
    }

    public componentDidMount(): void {
        this.onItemRenderComplete();
    }

    public componentDidUpdate(): void {
        this.onItemRenderComplete();
    }

    private rowRenderer = (index: number, item: WikiResult, details: IListItemDetails<WikiResult>): JSX.Element => {
        const { onFileNameClicked, renderLoadingRow } = this.props;
        const rowKey = "list-item" + index;
        const rowItemDetails = {
            ...details,
            onFocusItem: (rowIndex: number, event: React.FocusEvent<HTMLElement>) => {
                details.onFocusItem(rowIndex, event);
            }
        };

        return (
            <ListItem key={rowKey} details={rowItemDetails} className="search-item-list-row" index={index}>
                {
                    !item.hasOwnProperty("fileName")
                        ? renderLoadingRow()
                        : <WikiSearchResultRow
                            result={item}
                            sanitizeHtml={sanitizeHtml}
                            wikiPageUrl={this.getPageUrl(item)}
                            rowSubTitle={this.getSubtitle(item)}
                            onFileNameClicked={onFileNameClicked}
                        />
                }
            </ListItem>
        );
    }

    private getPageUrl(item: WikiResult): string {
        const wikiPath: string = this.removeMappedPathFromWikiPath(item);
        const fixedWikiPath: string = this.removeSpecialCharsFromWikiPath(wikiPath);

        const routeValues: { [key: string]: string } = {
            wikiIdentifier: item.wiki.name,
            wikiVersion: "GB" +  item.wiki.version,
            pagePath: fixedWikiPath,
            project: item.project.name
        };

        return routeUrl(this.props.pageContext, "ms.vss-wiki-web.wiki-overview-route", routeValues);
    }

    private getSubtitle(item: WikiResult): JSX.Element {
        return (
            <div>
                <FormatComponent format={Resources.InProjectString}>
                    <div className="project-and-wiki">
                        <span>{item.project.name}</span>
                        <span>{" > "}</span>
                        <span>{item.wiki.name}</span>
                    </div>
                </FormatComponent>
            </div>
        );
    }

    private onItemRenderComplete = (): void => {
        if (!this.isRendered && !this.props.itemsLoading) {
            this.isRendered = true;
            this.props.notifyOnRendered();
        }
    }

    /** Wiki path returned by the service needs following modifications:
    1. Removal of file extension
    2. Space is encoded as hyphen by the service. It needs to be reverted back to space.
    **/
    private removeSpecialCharsFromWikiPath(wikiPath: string): string {
        const fixedWikiPath: string = removeExtensionfromPagePath(wikiPath)
            .replace(UrlEscapeConstants.HyphenRegExp, UrlEscapeConstants.WhiteSpace);

        return fixedWikiPath;
    }

    /* Remove folder name contained in mapped path from wiki path.
    It is applicable only for code wiki */
    private removeMappedPathFromWikiPath(item: WikiResult): string {
        let mappedPath = item.wiki.mappedPath ? item.wiki.mappedPath : "/";
        if (!(mappedPath.length > 0 && mappedPath.charAt(mappedPath.length - 1) == "/")) {
            mappedPath = mappedPath.concat("/");
        }

        return item.path.substring(mappedPath.length - 1);
    }
}